import { Fragment, ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  Grid,
  SxProps,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow as MuiTableRow,
  Theme,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import { DataGrid } from '@components/dataGrid/Datagrid';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { TSortValue } from '@components/table/types/SortValue.type';
import { Pagination } from '@components/table/TablePagination';
import { GridRowHeightParams, GridRowHeightReturnValue, GridSortModel } from '@mui/x-data-grid';
import { TFilterValues, TTableBase } from '@components/table/types';
import { TableFilters, TableFiltersProps } from '@components/table/TableFilters';
import { NoResultsOverlaySlot, FooterSlot } from '@components/table/components';
import { handleTableFilterQueryParams, handleTableSortQueryParams } from '@components/table/utils';
import { NoResultsConfig } from '@components/table/components/NoResultsOverlaySlot';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { MobileRowSelectedView } from '@components/table/mobileComponents/MobileRowSelected';
import { formatColumnsForTable } from '@components/table/utils/formatColumnsForTable';
import { Dialog } from '@components/dialog/Dialog';
import { TwoArrowCollapse } from '@assets/svgs';
import { TableColumnSelection } from '@components/table/TableColumnSelection';
import { TableSortConfig } from '@components/table/utils/handleTableSortQueryParams';
import { alpha, Palette } from '@mui/material/styles';
import { getTypographyVariants } from '@theme/styleOverrides';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';

export type TableFilterConfig = {
  /** The way to map the applied filter to the query param for the api call */
  filterQueryParam: Record<
    string,
    string | ((value: string) => { key: string; value: string | string[] }[] | null)
  > | null;
  /** query params to be ignored for the table (IE something on the page that doesnt affect the table) */
  ignoreQueryParams?: string[];
  /** the actual filters for the table */
  filters?: TableFiltersProps['filters'];
  /** Default filters to be applied on render */
  defaultFilters?: TFilterValues;
  /** Filters controlled by the component outside of the Table */
  customFilters?: TFilterValues;
  /** Filters that will be restored to if "View all records" is clicked */
  resetToFilters?: TFilterValues;
  /** Handler to update anything in the wrapper component as needed when clicked */
  onResetFilters?: (resetToFilters?: TFilterValues) => void;

  /** default is inline if not overridden */
  displayMode?: 'Modal';
  /** custom callback handler for filter change */
  onFilterChange?: (key: string, value: any) => void;
};

type TableNoResultsConfig = NoResultsConfig & {
  getCustomMessageToShow?: ({ selectedFilters }: { selectedFilters: { [key: string]: any } }) =>
    | {
        message: string | undefined;
        showInsteadOfLoad?: boolean;
      }
    | undefined;
  getCustomResetToFilters?: ({ selectedFilters }: { selectedFilters: { [key: string]: any } }) => TFilterValues;
};
type TablePaginationConfig = {
  currentPage: number;
  rowsPerPage: number;
  pageOffset: number;
  onPageChange?: (page: number) => void;
};

type TableRowConfig<T extends TTableBase> = {
  rowHeight?: number;
  getRowId?: ({ row }: { row: T['items'][0] }) => string;
  onRowClick?: ({ row }: { row: T['items'][0] }) => void;
  getRowClassName?: ({
    row,
    nextRow,
    isLastVisible,
  }: {
    row: T['items'][0];
    nextRow: T['items'][0] | undefined;
    isLastVisible: boolean;
  }) => string;
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
};

type TableSlotsConfig = {
  Pagination?: {
    actionButton: ReactElement;
  };
  Toolbar?: () => ReactElement;
};

export type TableProps<T extends TTableBase = any> = {
  ariaLabel: string;
  tableId?: string;
  /** Should only be used when wrapped in a TableWithStore */
  syncToUrl?: boolean;
  columns: TableGridBaseColDef<T['items'][0]>[];
  isInitiating?: boolean;
  defaultQueryParams?: any;
  sortConfig?: TableSortConfig;
  noResultsConfig?: TableNoResultsConfig;
  filterConfig?: TableFilterConfig;
  paginationConfig?: TablePaginationConfig;
  hidePagination?: boolean;
  rowConfig?: TableRowConfig<T>;
  onLoadData: (
    params: T['params'],
    enabled: boolean,
  ) => {
    data?: {
      [x: string]: any;
      items: T['items'];
    };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };
  /** used if the api query result expects a single result */
  dataFormatter?: (data: any) => any[];
  /** used if the api query expects .items as a result */
  rowFormatter?: (rows: T['items']) => any[];
  sxProps?: {
    tableContainer?: SxProps<Theme>;
    dataGrid?: SxProps<Theme>;
    dataGridContainer?: SxProps<Theme>;
  };
  columnVisibilityModel?: any;
  slotsConfig?: TableSlotsConfig;
  fullScreenConfig?: {
    viewInFullScreen: boolean;
    onExitFullScreen: () => void;
  };

  columnConfig?: {
    /** only intended to be used for column selection which will not persist in the browser */
    enableColumnSelection?: boolean;
  };
  /** Config for expandable detail panel below the DataGrid. Only activates when provided — existing tables are unaffected. */
  detailPanelConfig?: {
    getDetailPanelContent: (row: T['items'][0]) => ReactNode;
    /** When true, multiple rows can be expanded simultaneously. Default: false (single row only). */
    allowMultipleExpanded?: boolean;
  };
  /** Disables internal scrolling — DataGrid expands to fit all rows. Use for tables embedded in scrollable page content. */
  autoHeight?: boolean;
};

export function Table<T extends TTableBase>({
  ariaLabel,
  tableId,
  defaultQueryParams,
  isInitiating = false,
  columns,
  noResultsConfig,
  filterConfig,
  paginationConfig,
  hidePagination,
  sortConfig,
  rowConfig,
  onLoadData,
  rowFormatter,
  dataFormatter,
  sxProps,
  columnVisibilityModel,
  slotsConfig,
  syncToUrl,
  fullScreenConfig,
  columnConfig,
  detailPanelConfig,
  autoHeight,
}: TableProps<T>) {
  const { isMobileScreen } = useGetScreenSize();
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();
  const [selectedRow, setSelectedRow] = useState<T['items'][0] | null>(null);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const detailTableRef = useRef<HTMLDivElement>(null);
  const index = columns.findIndex((col) => col?.mobileConfig?.mobileLockedColumn);
  const lockedIndex = index > -1 ? index : 0;

  const [visibleMobileColumnIndex, setVisibleMobileColumnIndex] = useState(lockedIndex + 1);
  const [pagination, setPagination] = useState<Pagination>(
    paginationConfig ?? { currentPage: 0, pageOffset: 0, rowsPerPage: 10 },
  );

  const [filterValues, setFilterValues] = useState<TFilterValues>(filterConfig?.defaultFilters ?? {});
  const [sortValues, setSortValues] = useState<TSortValue>(sortConfig?.defaultSort);

  const [queryParams, setQueryParams] = useState<any>({});
  const { currentPage, rowsPerPage, pageOffset } = pagination;
  const { onPageChange } = paginationConfig || {};

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    columns?.reduce(
      (acc, col) => {
        acc[col.field] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  const tableVisibleColumns = columnVisibilityModel ?? visibleColumns;

  const { data, isLoading, isFetching, isError } = onLoadData(
    {
      ...defaultQueryParams,
      ...queryParams?.filters,
      sort: queryParams?.sort?.queryValue,
      pageLimit: rowsPerPage,
      pageOffset,
    },
    true,
  );

  const totalItemCount = data?.pagination?.total ?? 0;

  const customMessage = useMemo(() => {
    return noResultsConfig?.getCustomMessageToShow
      ? noResultsConfig?.getCustomMessageToShow({ selectedFilters: filterValues })
      : undefined;
  }, [filterValues, noResultsConfig]);

  const handleClearAllFilters = () => {
    if (syncToUrl) {
      const FilterPolicyQueryParam = Object.keys(filterConfig?.filterQueryParam ?? {});
      const appliedFilterKeys = Object.keys(filterValues);
      appliedFilterKeys.forEach((key) => {
        // Check if the key is in FilterPolicyQueryParam
        if (FilterPolicyQueryParam.includes(key)) {
          // If it is, remove it from searchParamsInTheUrl
          if (filterConfig?.resetToFilters?.[key]) {
            searchParamsInTheUrl.set(key, filterConfig.resetToFilters[key]);
          } else {
            searchParamsInTheUrl.delete(key);
          }
        }
      });
      setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });
    }

    const resetToFilters = noResultsConfig?.getCustomResetToFilters
      ? noResultsConfig.getCustomResetToFilters({ selectedFilters: filterValues })
      : (filterConfig?.resetToFilters ?? {});

    setFilterValues(resetToFilters);
    filterConfig?.onResetFilters?.(resetToFilters);
    filterConfig?.onFilterChange?.('', null);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onPageChange?.(0);

    setPagination({
      currentPage: 0,
      pageOffset: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    });
  };

  const handleChangePage = useCallback(
    (page: number) => {
      onPageChange?.(page);

      setPagination((prev) => ({
        ...prev,
        currentPage: page,
        pageOffset: page * (pagination?.rowsPerPage || 0),
      }));
    },
    [pagination?.rowsPerPage, onPageChange],
  );

  const prevCustomFiltersRef = useRef(filterConfig?.customFilters);
  useEffect(() => {
    setFilterValues((cur) => {
      return { ...cur, ...filterConfig?.customFilters };
    });

    // Compare by value, not object identity: some callers pass an inline
    // `customFilters` literal (e.g. ManageAutomaticPaymentsView), so unrelated
    // state changes recreate the object without changing the filter itself.
    if (isEqual(prevCustomFiltersRef.current, filterConfig?.customFilters)) {
      return;
    }
    prevCustomFiltersRef.current = filterConfig?.customFilters;

    // Quick/custom filters change the result set, so reset to the first page -
    // otherwise applying a filter from a later page can land on an out-of-range
    // (empty) page even when matching rows exist.
    setPagination((prev) => ({ ...prev, currentPage: 0, pageOffset: 0 }));
  }, [filterConfig?.customFilters]);

  const handleFilterChange = (key: string, value: any) => {
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      [key]: value,
    }));

    filterConfig?.onFilterChange?.(key, value);

    setPagination({ currentPage: 0, pageOffset: 0, rowsPerPage: pagination.rowsPerPage });
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!sortModel?.length) {
      setSortValues(undefined);
    } else {
      const sortItem = sortModel[0];
      const sortOrder = sortItem.sort === 'asc' ? 'asc' : 'desc';

      setSortValues({ field: sortItem.field, sort: sortOrder });
    }

    setPagination({ currentPage: 0, pageOffset: 0, rowsPerPage: pagination.rowsPerPage });
  };

  useEffect(() => {
    handleTableFilterQueryParams({
      onSetQueryParams: setQueryParams,
      filterValues,
      filterQueryParam: filterConfig?.filterQueryParam,
      ignoreQueryParams: filterConfig?.ignoreQueryParams,
    });
  }, [filterConfig, filterValues]);

  const sortConfigPrefixValue = sortConfig?.sortConfigPrefix
    ? filterValues?.[sortConfig.sortConfigPrefix.filterKey]
    : undefined;

  useEffect(() => {
    if (!sortConfig) {
      return;
    }

    sortConfig?.onSortChange?.();

    handleTableSortQueryParams({
      sortValues,
      sortConfig,
      sortConfigPrefixValue,
      syncToUrl,
      onSetQueryParams: setQueryParams,
      searchParamsInTheUrl,
      onSetSearchParamsInTheUrl: setSearchParamsInTheUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig?.sortQueryParam, sortValues, sortConfigPrefixValue]);

  const rows: T['items'] = useMemo(() => {
    if (!data?.items) {
      if (dataFormatter) {
        return dataFormatter(data);
      }
      return [];
    }

    const resultingRows = rowFormatter ? rowFormatter([...data.items]) : data.items;

    if (selectedRow) {
      if (!resultingRows.find((row) => row.id === selectedRow.id)) {
        setSelectedRow(null);
      }
    }

    return resultingRows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataFormatter, rowFormatter]);

  // Collapse expanded rows that are no longer in the result set
  useEffect(() => {
    if (expandedRowIds.size === 0 || !rows.length) return;
    const validIds = new Set(rows.map((row: any) => (rowConfig?.getRowId ? rowConfig.getRowId({ row }) : row.id)));
    const filtered = [...expandedRowIds].filter((id) => validIds.has(id));
    if (filtered.length < expandedRowIds.size) {
      setExpandedRowIds(new Set(filtered));
    }
  }, [rows, expandedRowIds, rowConfig]);

  const dataGridStyles = sxProps?.dataGrid ? sxProps.dataGrid : {};

  const isLoadingTable = (isInitiating || isLoading || isFetching || isError) && !customMessage?.showInsteadOfLoad;
  const selectedSortColumnIsSortable = useMemo(() => {
    return columns?.find((col) => col.field === sortValues?.field)?.sortable !== false;
  }, [columns, sortValues]);

  const Footer = useCallback(
    () => (
      <FooterSlot
        slotsConfig={slotsConfig}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        totalItemCount={totalItemCount}
        rowsPerPageOptions={isMobileScreen ? [4] : undefined}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slotsConfig?.Pagination?.actionButton, currentPage, isMobileScreen, handleChangePage, rowsPerPage, totalItemCount],
  );

  const NoResultsOverlayView = useCallback(() => {
    const updatedFilters = {
      ...filterValues,
    };
    delete updatedFilters?.debugMode;
    delete updatedFilters?.sort;
    return (
      <NoResultsOverlaySlot
        {...noResultsConfig}
        filterValues={customMessage?.message ? {} : updatedFilters}
        isLoading={isInitiating || isLoadingTable}
        noResultsMessage={customMessage?.message ?? noResultsConfig?.noResultsMessage}
        onClearAllFilters={handleClearAllFilters}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noResultsConfig, filterValues, customMessage, isLoadingTable]);

  const columnsToDisplay = useMemo(() => {
    if (!isMobileScreen) {
      setSelectedRow(null);
      return columns;
    }

    return columns?.filter((col) => col?.mobileConfig?.hidden?.xs !== true);
  }, [columns, isMobileScreen]);

  // Compute pixel column widths for the detail panel MUI Table (mirrors DataGrid flex algorithm)
  const [detailTableWidth, setDetailTableWidth] = useState(0);
  useEffect(() => {
    const el = detailTableRef.current;
    if (!detailPanelConfig || !el) return;
    setDetailTableWidth(el.offsetWidth);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setDetailTableWidth(Math.round(w));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [detailPanelConfig]);

  const detailColWidths = useMemo(() => {
    if (!detailTableWidth || !detailPanelConfig) return null;
    const expandWidth = 40;
    const visibleCols = columnsToDisplay.filter((col) => tableVisibleColumns[col.field] !== false);
    const totalFixed = visibleCols.reduce(
      (sum, col) => sum + (col.flex == null || col.flex <= 0 ? ((col.width as number) ?? 100) : 0),
      expandWidth,
    );
    const totalFlex = visibleCols.reduce((sum, col) => sum + (col.flex != null && col.flex > 0 ? col.flex : 0), 0);
    const remaining = Math.max(0, detailTableWidth - totalFixed);
    return Object.fromEntries(
      visibleCols.map((col) => {
        const hasFlex = col.flex != null && col.flex > 0;
        return [col.field, Math.round(hasFlex ? (remaining * col.flex!) / totalFlex : ((col.width as number) ?? 100))];
      }),
    );
  }, [detailTableWidth, detailPanelConfig, columnsToDisplay, tableVisibleColumns]);

  useEffect(() => {
    setPagination({
      currentPage: 0,
      pageOffset: 0,
      // TODO/FUTURE: potentially pull from pagination mobile config (so can change per table)
      rowsPerPage: isMobileScreen ? 4 : (paginationConfig?.rowsPerPage ?? 10),
    });
  }, [isMobileScreen, paginationConfig?.rowsPerPage]);

  const TableComponent = (
    <Grid
      container
      height='100%'
      width='100%'
      sx={{
        ...(fullScreenConfig ? { alignContent: 'baseline' } : {}),
        ...(sxProps?.tableContainer ? sxProps.tableContainer : {}),
      }}
      id='table-container'
    >
      {filterConfig?.filters || columnConfig?.enableColumnSelection ? (
        <Grid
          sx={{
            display: 'flex',
            width: '100%',
          }}
        >
          {/* filters */}
          {filterConfig && filterConfig.filters && (
            <Grid size={{ xs: 12 }} sx={{ flex: 1 }}>
              <TableFilters
                {...filterConfig}
                isMobileScreen={isMobileScreen}
                selectedValues={filterValues}
                onFilterChange={(key: string, value: any) => handleFilterChange(key, value)}
              />
            </Grid>
          )}
          {/* 'dumb' (not persistent) column selection */}
          {columnConfig?.enableColumnSelection && (
            <Grid size={{ xs: 12 }} sx={{ width: '20px' }}>
              <TableColumnSelection
                columns={columnsToDisplay}
                visibleColumns={tableVisibleColumns}
                onUpdateVisibleColumns={(updater) => {
                  setVisibleColumns((prev) => ({
                    ...prev,
                    ...updater(prev),
                  }));
                }}
              />
            </Grid>
          )}
        </Grid>
      ) : null}

      <Grid size={{ xs: 12 }} sx={sxProps?.dataGridContainer}>
        {detailPanelConfig ? (
          /* MUI Table with inline expandable rows — only when detailPanelConfig is provided */
          <Box sx={[sxStyles.dataGridBox, sxStyles.detailPanelTable, dataGridStyles as any]}>
            <TableContainer ref={detailTableRef}>
              <MuiTable size='small' aria-label={ariaLabel}>
                <TableHead>
                  <MuiTableRow>
                    <TableCell sx={{ width: 40, p: 0.5 }} />
                    {columnsToDisplay
                      .filter((col) => tableVisibleColumns[col.field] !== false)
                      .map((col) => (
                        <TableCell
                          key={col.field}
                          sx={{
                            fontWeight: 600,
                            width: detailColWidths?.[col.field] ?? col.width,
                          }}
                        >
                          {col.headerName}
                        </TableCell>
                      ))}
                  </MuiTableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const rowId = rowConfig?.getRowId ? rowConfig.getRowId({ row }) : row.id;
                    const isExpanded = expandedRowIds.has(rowId);
                    return (
                      <Fragment key={rowId}>
                        <MuiTableRow
                          hover
                          sx={{
                            cursor: 'pointer',
                            ...(isExpanded && {
                              backgroundColor: 'grey.100',
                              '& > *': { borderBottom: 'unset' },
                            }),
                          }}
                          onClick={() =>
                            setExpandedRowIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(rowId)) {
                                next.delete(rowId);
                              } else {
                                if (!detailPanelConfig.allowMultipleExpanded) next.clear();
                                next.add(rowId);
                              }
                              return next;
                            })
                          }
                        >
                          <TableCell sx={{ p: 0.5, textAlign: 'center' }}>
                            <Box
                              component='span'
                              sx={{
                                display: 'inline-block',
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                fontSize: '0.7rem',
                                lineHeight: 1,
                              }}
                            >
                              ▶
                            </Box>
                          </TableCell>
                          {columnsToDisplay
                            .filter((col) => tableVisibleColumns[col.field] !== false)
                            .map((col) => {
                              const value = col.valueGetter
                                ? col.valueGetter(undefined as any, row)
                                : row[col.field as keyof typeof row];
                              const content = col.renderCell ? col.renderCell({ row, value }) : (value ?? '-');
                              return (
                                <TableCell key={col.field}>
                                  <OverflowTooltip>
                                    {typeof content === 'string' || typeof content === 'number'
                                      ? String(content)
                                      : (content as React.ReactElement)}
                                  </OverflowTooltip>
                                </TableCell>
                              );
                            })}
                        </MuiTableRow>
                        <MuiTableRow className='detail-panel-collapse-row'>
                          <TableCell
                            colSpan={
                              columnsToDisplay.filter((col) => tableVisibleColumns[col.field] !== false).length + 1
                            }
                            sx={{
                              p: 0,
                              ...(isExpanded
                                ? { pb: 2, pl: 7, borderBottom: '3px solid', borderColor: 'grey.400' }
                                : { border: 'none' }),
                            }}
                          >
                            <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                              {detailPanelConfig.getDetailPanelContent(row)}
                            </Collapse>
                          </TableCell>
                        </MuiTableRow>
                      </Fragment>
                    );
                  })}
                  {rows.length === 0 && !isLoadingTable && (
                    <MuiTableRow>
                      <TableCell
                        colSpan={columnsToDisplay.length + 1}
                        sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
                      >
                        {noResultsConfig?.noResultsMessage ?? 'No items to display'}
                      </TableCell>
                    </MuiTableRow>
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>
            {!hidePagination && <Footer />}
          </Box>
        ) : (
          /* Standard DataGrid rendering */
          <Box
            id={tableId ?? ariaLabel ?? undefined}
            sx={
              isMobileScreen
                ? {
                    ...sxStyles.dataGridBox,
                    ' .MuiDataGrid-columnHeaderTitleContainerContent': {
                      width: '100% ',
                    },
                  }
                : sxStyles.dataGridBox
            }
          >
            <DataGrid
              data-testid={`${tableId}-${isLoadingTable ? 'loading' : 'loaded'}`}
              aria-label={ariaLabel}
              autoHeight={autoHeight}
              columnVisibilityModel={tableVisibleColumns}
              getRowHeight={rowConfig?.getRowHeight ?? undefined}
              getRowId={(row) => {
                return rowConfig?.getRowId ? rowConfig?.getRowId({ row }) : row.id;
              }}
              sortModel={
                sortValues && selectedSortColumnIsSortable ? [{ field: sortValues.field, sort: sortValues.sort }] : []
              }
              loading={isLoadingTable}
              slots={{
                footer: hidePagination ? () => <div /> : Footer,
                noResultsOverlay: NoResultsOverlayView, // when there are no results
                noRowsOverlay: NoResultsOverlayView, // when there are no items
                toolbar: slotsConfig?.Toolbar ?? undefined,
              }}
              getCellClassName={(e) => {
                return e.field === sortValues?.field ? 'sorted-column' : '';
              }}
              onRowClick={({ row }) => {
                if (rowConfig?.onRowClick) {
                  rowConfig.onRowClick({ row });
                  return;
                }
                if (isMobileScreen) {
                  setSelectedRow(row);
                }
              }}
              getRowClassName={({ row, indexRelativeToCurrentPage, isLastVisible }) => {
                const nextRow = rows[indexRelativeToCurrentPage + 1];
                return rowConfig?.getRowClassName?.({ row, nextRow, isLastVisible }) ?? '';
              }}
              localeText={{ noRowsLabel: noResultsConfig?.noResultsMessage ?? 'No items to display' }}
              paginationModel={{ page: currentPage, pageSize: rowsPerPage }}
              disableColumnSorting={isMobileScreen}
              columns={formatColumnsForTable({
                columnVisibilityModel: tableVisibleColumns,
                columns: columnsToDisplay,
                isMobileScreen,
                visibleMobileColumnIndex,
                onSetSelectedRow: setSelectedRow,
                onSetVisibleMobileColumnIndex: setVisibleMobileColumnIndex,
              })}
              rows={rows}
              rowCount={totalItemCount ?? 0}
              initialState={{ density: 'compact' }}
              onSortModelChange={(sortModel: GridSortModel) => handleSortModelChange(sortModel)}
              sx={{
                ...styles.dataGrid,
                '& .MuiDataGrid-row:last-child': {
                  borderBottomWidth: '1px',
                  borderColor: 'border.default',
                  borderBottomStyle: 'solid',
                },
                '.sorted-column': {
                  fontWeight: selectedSortColumnIsSortable ? '700' : 'normal',
                },

                '.last-highlight-row': {
                  borderBottomWidth: '3px',
                  borderColor: 'border.default',
                  borderBottomStyle: 'inset',
                },

                '.blue-highlight-row': {
                  backgroundColor: ({ palette }: { palette: Palette }) =>
                    alpha((palette.secondary as any).alt, isHolidayTheme(palette) ? 0.25 : 0.6),
                  '&:hover': {
                    backgroundColor: ({ palette }: { palette: Palette }) =>
                      alpha((palette.secondary as any).alt, isHolidayTheme(palette) ? 0.35 : 0.4),
                  },
                },

                ' .yellow-highlight-row': {
                  backgroundColor: ({ palette }: { palette: Palette }) =>
                    isHolidayTheme(palette) ? alpha(palette.warning.main, 0.25) : palette.warning.main,
                  '&:hover': {
                    backgroundColor: ({ palette }: { palette: Palette }) =>
                      isHolidayTheme(palette) ? alpha(palette.warning.main, 0.35) : palette.warning.light,
                  },
                },

                ' .deleted-row': {
                  pointerEvents: 'none',
                },
                ...(rowConfig?.onRowClick || isMobileScreen ? sxStyles.clickableRow : {}),
                ...dataGridStyles,
              }}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );

  if (fullScreenConfig?.viewInFullScreen) {
    return (
      <Dialog
        open={true}
        id='full-screen-table-dialog'
        sx={{ flex: 'none', alignContent: 'baseline' }}
        title='Offering Field Search'
        closeIcon={<TwoArrowCollapse />}
        fullScreen={true}
        handleCloseDialog={fullScreenConfig.onExitFullScreen}
        primaryBtnText=''
        secondaryBtnText='Close'
        secondaryBtnAction={fullScreenConfig.onExitFullScreen}
        size='fullWidth'
      >
        <Box sx={{ padding: 2 }}>{TableComponent}</Box>
      </Dialog>
    );
  }
  return (
    <>
      <MobileRowSelectedView
        columnVisibilityModel={tableVisibleColumns}
        columns={columns}
        row={selectedRow}
        onClose={() => setSelectedRow(null)}
      />
      {TableComponent}
    </>
  );
}

// holiday themes are dark with orange accents, so full-strength highlight colors swallow the orange text
const isHolidayTheme = (palette: Palette) => ['halloweenPalette', 'aprilFoolsPalette'].includes(palette.theme);

const sxStyles = {
  dataGridBox: {
    position: 'relative',
    height: '100%',
    width: '100%',
    backgroundColor: (theme: Theme) => theme.palette.dataGrid?.background ?? theme.palette.background.light,
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
    borderBottomLeftRadius: '8px',
  },
  clickableRow: {
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: (theme: Theme) => `${theme.palette.secondary.light}30`,
      },
    },
  },
  /** Styles that make MUI Table visually match the DataGrid compact density */
  detailPanelTable: {
    // Table layout: fixed so header cell widths control all columns
    '& .MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
    },
    // Header cells — match DataGrid columnHeaderTitle
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontSize: (theme: Theme) => getTypographyVariants((theme.palette as any).fontSize).tableHeader.fontSize,
      lineHeight: (theme: Theme) => getTypographyVariants((theme.palette as any).fontSize).tableHeader.lineHeight,
      textTransform: 'uppercase' as const,
      color: 'text.subdued',
      fontWeight: (theme: Theme) => getTypographyVariants((theme.palette as any).fontSize).fontWeightBold,
      borderColor: (theme: Theme) => theme.palette.border.main,
      whiteSpace: 'nowrap',
      padding: '8px 10px',
      letterSpacing: '0.03em',
    },
    // Body rows — match DataGrid compact density (36px row height)
    '& .MuiTableBody-root > .MuiTableRow-root:not(.detail-panel-collapse-row)': {
      height: 36,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'inherit',
      },
    },
    // Body cells — match DataGrid compact density
    '& .MuiTableBody-root .MuiTableCell-root': {
      fontWeight: 'normal',
      borderColor: (theme: Theme) => theme.palette.border.main,
      padding: '0 10px',
      overflow: 'hidden',
      '&:focus': { outline: 'none' },
    },
    // Pagination
    '& .MuiTablePagination-root': {
      borderBottom: 'none',
    },
  },
};
